/**
 * Match Card — dense, single-glance layout optimised for long fixture lists.
 *
 * Structure:
 *   ┌──────────────────────────────────────────────┐
 *   │ #1 · LEAGUE · ● COMPLETED      Tue Apr 21 · 4:00 PM
 *   │──────────────────────────────────────────────
 *   │ 🏆 TEAM A         40/2 (3.0)
 *   │    TEAM D          1/4 (0.4)
 *   │──────────────────────────────────────────────
 *   │ 🏆 TEAM A won by 39 runs
 *   │ 📍 Powai Ground · ⚽ Turf 1         [primary btn] [⋯]
 *   └──────────────────────────────────────────────┘
 */
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import EditFixtureModal from './EditFixtureModal';
import { roundLabel as nameRound } from './rounds';

const STATUS_STYLES = {
  scheduled: { dot: 'bg-blue-400', text: 'text-blue-600', label: 'Scheduled' },
  live: { dot: 'bg-red-500 animate-pulse', text: 'text-red-600', label: 'Live' },
  completed: { dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Completed' },
  postponed: { dot: 'bg-amber-400', text: 'text-amber-600', label: 'Postponed' },
  cancelled: { dot: 'bg-gray-400', text: 'text-gray-500', label: 'Cancelled' },
};

function formatDate(dateStr) {
  if (!dateStr) return 'TBD';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function formatOvers(overs) {
  if (overs === null || overs === undefined) return '-';
  const full = Math.floor(overs);
  const balls = Math.round((overs - full) * 10);
  return balls > 0 ? `${full}.${balls}` : `${full}`;
}

function formatScore(score, wickets, overs) {
  if (score === null || score === undefined) return null;
  return `${score}/${wickets || 0} (${formatOvers(overs)})`;
}

// One team row inside the card body
function TeamRow({ logo, name, score, isWinner, isTBD }) {
  return (
    <div className="flex items-center justify-between gap-3 min-w-0">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {isWinner && (
          <i className="ph-fill ph-trophy text-amber-500 text-sm flex-shrink-0" aria-hidden />
        )}
        {logo ? (
          <img src={logo} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-6 h-6 bg-[#2B3A67]/10 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ph-bold ph-users text-[#2B3A67] text-[10px]"></i>
          </div>
        )}
        <span className={`truncate text-sm ${isWinner ? 'font-bold text-[#2B3A67]' : isTBD ? 'text-gray-400' : 'text-gray-800'}`}>
          {name || 'TBD'}
        </span>
      </div>
      {score && (
        <span className={`font-mono text-sm tabular-nums flex-shrink-0 ${isWinner ? 'font-bold text-[#2B3A67]' : 'text-gray-700'}`}>
          {score}
        </span>
      )}
    </div>
  );
}

export default function MatchCard({ fixture, isAdmin, eventId, onRecordResult, onQuickResult, onRevertResult, onReopenMatch, onRefinaliseMatch, onDelete, onAssignScorer, onRefresh }) {
  const navigate = useNavigate();
  const [showEdit, setShowEdit] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); };
  }, [menuOpen]);

  const status = STATUS_STYLES[fixture.match_status] || STATUS_STYLES.scheduled;
  const roundLabel = nameRound(fixture, { short: true });
  const isCompleted = fixture.match_status === 'completed';
  const isLive = fixture.match_status === 'live';
  const isScheduled = fixture.match_status === 'scheduled';
  const isLiveScored = !!fixture.is_live_scored;
  // Drifted = has live-scoring data but status is stuck at scheduled
  // (caused by legacy revert-on-live-match bug). Offer one-click re-finalise.
  const isDrifted = isScheduled && isLiveScored;
  const hasBothTeams = !!(fixture.team_a_id && fixture.team_b_id);
  const isWinner = (teamId) => fixture.winner_team_id === teamId;
  const showScores = isCompleted || isLive;

  const scoreA = showScores ? formatScore(fixture.team_a_score, fixture.team_a_wickets, fixture.team_a_overs) : null;
  const scoreB = showScores ? formatScore(fixture.team_b_score, fixture.team_b_wickets, fixture.team_b_overs) : null;

  const goToScorecard = () => navigate(`/tournaments/${eventId}/match/${fixture.id}`);
  const stop = (fn) => (e) => { e.stopPropagation(); fn?.(); };

  // Primary inline action: one most-relevant button per status
  const primaryAction = (() => {
    if (!isAdmin) return null;
    if (isLive) {
      return { label: 'Score', icon: 'ph-broadcast', className: 'bg-red-500 text-white hover:bg-red-600 animate-pulse',
        onClick: () => navigate(`/tournaments/${eventId}/live-scoring/${fixture.id}`),
        testId: `continue-scoring-btn-${fixture.match_number}` };
    }
    if (isScheduled && hasBothTeams) {
      return { label: 'Start', icon: 'ph-play', className: 'bg-[#E54562] text-white hover:bg-[#d13a55]',
        onClick: () => navigate(`/tournaments/${eventId}/live-scoring/${fixture.id}`),
        testId: `start-scoring-btn-${fixture.match_number}` };
    }
    if (isCompleted) {
      if (isLiveScored) {
        return { label: 'Reopen', icon: 'ph-arrow-u-up-left', className: 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100',
          onClick: onReopenMatch, testId: `reopen-match-btn-${fixture.match_number}` };
      }
      return { label: 'Revert', icon: 'ph-arrow-counter-clockwise', className: 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100',
        onClick: onRevertResult, testId: `revert-result-btn-${fixture.match_number}` };
    }
    if (isDrifted) {
      return { label: 'Re-finalise', icon: 'ph-flag-checkered', className: 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100',
        onClick: onRefinaliseMatch, testId: `refinalise-match-btn-${fixture.match_number}` };
    }
    return null;
  })();

  // Secondary actions (go into the overflow menu)
  const menuActions = [];
  if (isAdmin) {
    if ((isScheduled || isLive) && hasBothTeams) {
      menuActions.push({ label: 'Assign Scorer', icon: 'ph-user-plus', onClick: () => onAssignScorer?.(fixture),
        testId: `assign-scorer-btn-${fixture.match_number}` });
    }
    if ((isScheduled || isLive) && hasBothTeams) {
      menuActions.push({ label: 'Quick Result', icon: 'ph-flag-checkered', onClick: onQuickResult,
        testId: `quick-result-btn-${fixture.match_number}` });
    }
    if (isScheduled && hasBothTeams) {
      menuActions.push({ label: 'Manual Scores', icon: 'ph-note-pencil', onClick: onRecordResult,
        testId: `record-result-btn-${fixture.match_number}` });
    }
    if (isScheduled) {
      menuActions.push({ label: 'Edit Schedule', icon: 'ph-calendar-blank', onClick: () => setShowEdit(true),
        testId: `edit-schedule-btn-${fixture.match_number}` });
    }
    menuActions.push({ label: 'Delete Fixture', icon: 'ph-trash', onClick: onDelete, danger: true,
      testId: `delete-fixture-btn-${fixture.match_number}` });
  }

  return (
    <div
      className={`bg-white border rounded-xl overflow-hidden transition-all cursor-pointer hover:shadow-md hover:border-[#2B3A67]/20 ${
        isLive ? 'border-red-200' : 'border-gray-100'
      }`}
      data-testid={`match-card-${fixture.match_number}`}
      onClick={goToScorecard}
    >
      {/* HEADER — single compact line */}
      <div className="px-3.5 py-2 bg-gray-50/70 border-b border-gray-100 flex items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-bold text-gray-500 flex-shrink-0">#{fixture.match_number}</span>
          <span className="text-gray-300">·</span>
          <span className="font-medium text-[#2B3A67] flex-shrink-0">
            {roundLabel}{fixture.group_name ? ` ${fixture.group_name}` : ''}
          </span>
          <span className="text-gray-300">·</span>
          <span className={`flex items-center gap-1 font-medium ${status.text} flex-shrink-0`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
            {status.label}
          </span>
        </div>
        <span className="text-gray-400 truncate flex-shrink-0">{formatDate(fixture.scheduled_at)}</span>
      </div>

      {/* TEAMS — stacked rows, logo+name left, score right */}
      <div className="px-3.5 py-2.5 space-y-1.5">
        <TeamRow
          logo={fixture.team_a_logo}
          name={fixture.team_a_name}
          score={scoreA}
          isWinner={isWinner(fixture.team_a_id)}
          isTBD={!fixture.team_a_name}
        />
        <TeamRow
          logo={fixture.team_b_logo}
          name={fixture.team_b_name}
          score={scoreB}
          isWinner={isWinner(fixture.team_b_id)}
          isTBD={!fixture.team_b_name}
        />
      </div>

      {/* FOOTER — result + venue/turf + inline actions */}
      {(fixture.result_summary || fixture.venue_name || fixture.turf_name || primaryAction || menuActions.length > 0) && (
        <div className="px-3.5 py-2 bg-gray-50/40 border-t border-gray-100 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1 space-y-0.5">
            {fixture.result_summary && (
              <div className="flex items-center gap-1 text-[11px] font-semibold text-[#3AAFA9] truncate">
                <i className="ph-fill ph-trophy text-amber-500 text-[10px]"></i>
                <span className="truncate">{fixture.result_summary}</span>
              </div>
            )}
            {(fixture.venue_name || fixture.turf_name) && (
              <div className="flex items-center gap-2 text-[10px] text-gray-400 truncate">
                {fixture.venue_name && (
                  <span className="flex items-center gap-0.5 truncate">
                    <i className="ph-bold ph-map-pin"></i>
                    <span className="truncate">{fixture.venue_name}</span>
                  </span>
                )}
                {fixture.turf_name && (
                  <>
                    {fixture.venue_name && <span className="text-gray-300">·</span>}
                    <span className="flex items-center gap-0.5 truncate">
                      <i className="ph-bold ph-soccer-ball"></i>
                      <span className="truncate">{fixture.turf_name}</span>
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Admin inline actions */}
          {isAdmin && (primaryAction || menuActions.length > 0) && (
            <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {primaryAction && (
                <button
                  onClick={stop(primaryAction.onClick)}
                  className={`text-[11px] font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${primaryAction.className}`}
                  data-testid={primaryAction.testId}
                >
                  <i className={`ph-bold ${primaryAction.icon}`}></i>{primaryAction.label}
                </button>
              )}
              {menuActions.length > 0 && (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={stop(() => setMenuOpen(v => !v))}
                    className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500"
                    data-testid={`match-menu-btn-${fixture.match_number}`}
                    aria-label="More actions"
                  ><i className="ph-bold ph-dots-three-vertical"></i></button>
                  {menuOpen && (
                    <div className="absolute right-0 bottom-full mb-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1" data-testid={`match-menu-${fixture.match_number}`}>
                      {menuActions.map((a) => (
                        <button
                          key={a.label}
                          onClick={stop(() => { setMenuOpen(false); a.onClick?.(); })}
                          className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-gray-50 ${a.danger ? 'text-red-600' : 'text-gray-700'}`}
                          data-testid={a.testId}
                        >
                          <i className={`ph-bold ${a.icon} text-sm`}></i>{a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showEdit && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          data-testid="edit-fixture-modal-wrapper"
        >
          <EditFixtureModal
            fixture={fixture}
            eventId={eventId}
            onClose={() => setShowEdit(false)}
            onSaved={() => { setShowEdit(false); onRefresh && onRefresh(); }}
          />
        </div>
      )}
    </div>
  );
}
